import React, { useState, forwardRef, useCallback, useMemo, useEffect } from "react";
import styles from "./Placeholder.module.css";
import Select from 'react-select';
import chroma from 'chroma-js';
import { ElegantTimePicker } from "../../timepicker/TimePicker";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const Placeholder = forwardRef(({
  label,
  type = "text",
  name,
  value = "",
  onChange,
  readOnly = false,
  onFocus,
  onBlur,
  placeholder = "",
  required,
  className = "",
  options = [],
  transformValue = "-0.9vh",
  classNamePrefix = "",
  componentsx = "",
  stylesx = null,
  isMulti = false,
  disabled = false,
  country = "BR",
  error = false,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState(country);

  useEffect(() => {
    setPhoneCountry(country);
  }, [country]);

  const isActive = useMemo(() => isFocused || Boolean(value), [isFocused, value]);

  const handleFocus = useCallback((e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  }, [onBlur]);

  const toggleMostrarSenha = useCallback((e) => {
    e.preventDefault();
    setMostrarSenha(prevState => !prevState);
  }, []);

  const propagatePhoneChange = useCallback((number, countryCode) => {
    if (onChange) {
      const isValid = isValidPhoneNumber(number || '', countryCode);
      const event = {
        target: {
          name: name,
          value: number === undefined ? '' : number,
          phoneContext: {
            number: number === undefined ? '' : number,
            country: countryCode,
            isValid: isValid,
          }
        },
      };
      onChange(event);
    }
  }, [onChange, name]);

  const handlePhoneValueChange = useCallback((phoneNumber) => {
    propagatePhoneChange(phoneNumber, phoneCountry);
  }, [phoneCountry, propagatePhoneChange]);

  const handlePhoneCountryChange = useCallback((newCountry) => {
    if (newCountry) {
      setPhoneCountry(newCountry);
      propagatePhoneChange(value, newCountry);
    }
  }, [value, propagatePhoneChange]);

  const customStyles = useMemo(() => {
    return {
      menuPortal: base => ({ ...base, zIndex: 9999 }),
      control: (styles, state) => ({
        ...styles,
        backgroundColor: state.isDisabled ? 'gray' : 'white',
        boxShadow: 'none',
        '&:hover': { borderColor: error ? 'var(--color-danger-alt, var(--danger-color, #dc3545))' : 'var(--form-label-active-color)' },
        border: error ? '1px solid var(--color-danger-alt, var(--danger-color, #dc3545))' : (state.isDisabled ? '1px solid #383838' : 'none'),
        borderRadius: '10px',
        cursor: state.isDisabled ? 'not-allowed' : 'default'
      }),
      valueContainer: (styles) => ({
        ...styles,
        paddingTop: '15px',
        paddingBottom: '15px',
      }),
      menu: (styles) => ({
        ...styles,
        zIndex: 200
      }),
      option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        const color = chroma(data.value?.color || 'black');
        return {
          ...styles,
          backgroundColor: isDisabled ? undefined :
            isSelected ? data.value?.color :
              isFocused ? color.alpha(0.1).css() : undefined,
          color: isDisabled
            ? '#ccc'
            : isSelected
              ? chroma.contrast(color, 'blue') > 2
                ? 'blue'
                : 'black'
              : data.value?.color || "black",
          cursor: isDisabled ? 'not-allowed' : 'default',

          ':active': {
            ...styles[':active'],
            backgroundColor: !isDisabled ? isSelected ? data.value?.color : color.alpha(0.3).css() : undefined,
          },
        };
      },
      multiValue: (styles, { data }) => {
        const color = chroma(data.value?.color || 'black');
        return {
          ...styles,
          backgroundColor: color.alpha(0.1).css(),
        };
      },
      multiValueLabel: (styles, { data }) => ({
        ...styles,
        color: data.value?.color || "black",
      }),
      multiValueRemove: (styles, { data }) => ({
        ...styles,
        color: data.value?.color || "black",
        ':hover': {
          backgroundColor: data.value?.color,
          color: 'white',
        },
      }),
      ...stylesx
    };
  }, [stylesx, error]);

  const labelStyle = useMemo(() =>
    type === "checkbox" ? {} :
      isActive ? { transform: `translateY(${transformValue})` } : {}
    , [isActive, transformValue, type]);

  const holderClassName = useMemo(() =>
    `${styles.holder} ${type !== "checkbox" && isActive ? styles.active : ""} ${error ? styles.errorHolder : ""} ${className}`
    , [isActive, className, type, error]);

  const inputElement = useMemo(() => {
    const commonProps = {
      ref,
      name,
      required,
      onFocus: handleFocus,
      onBlur: handleBlur,
    };

    if (type === "textarea") {
      return (
        <textarea
          {...commonProps}
          className={styles.form_control}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={placeholder}
          rows="3"
          disabled={disabled}
          {...props}
        />
      );
    }

    if (type === "select") {
      return (
        <Select
          {...commonProps}
          placeholder={placeholder}
          className={styles.form_control}
          classNamePrefix={classNamePrefix}
          styles={customStyles}
          value={value}
          components={componentsx}
          onChange={onChange}
          options={options}
          isMulti={isMulti}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          loadingMessage={() => "Buscando opções... Por favor, aguarde."}
          noOptionsMessage={() => "Nenhuma opção encontrada."}
          {...props}
        />
      );
    }

    if (type === "tel") {
      return (
        <div className={styles.phone_input_container}>
          <PhoneInput
            {...commonProps}
            placeholder={placeholder}
            value={value}
            onChange={handlePhoneValueChange}
            onCountryChange={handlePhoneCountryChange}
            country={phoneCountry}
            international
            className={styles.form_control}
            disabled={disabled}
            flagUrl="/icons/flags/{XX}.svg"
            {...props}
          />
        </div>
      );
    }

    if (type === "password") {
      return (
        <div className={styles.password_container}>
          <input
            {...commonProps}
            type={mostrarSenha ? "text" : "password"}
            className={`${styles.form_control} ${styles.password_input}`}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            aria-label={label}
            disabled={disabled}
            {...props}
          />
          <button
            type="button"
            className={styles.toggle_password}
            onClick={toggleMostrarSenha}
            aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
          >
            {mostrarSenha ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
              </svg>
            )}
          </button>
        </div>
      );
    }

    if (type === 'time') {
      return (
        <ElegantTimePicker
          name={name}
          value={value}
          onChange={onChange}
        />
      )
    }

    return (
      <input
        {...commonProps}
        type={type}
        className={styles.form_control}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-label={label}
        disabled={disabled}
        {...props}
      />
    );
  }, [
    type, ref, name, required, handleFocus, handleBlur,
    value, onChange, placeholder, props,
    classNamePrefix, customStyles, componentsx, options, isMulti,
    mostrarSenha, toggleMostrarSenha, label, disabled, readOnly,
    phoneCountry, handlePhoneValueChange, handlePhoneCountryChange
  ]);

  return (
    <div className={holderClassName}>
      {inputElement}
      <label className={styles.form_label} style={labelStyle} disabled={disabled}>{label}</label>
    </div>
  );
});

Placeholder.displayName = 'Placeholder';

export default React.memo(Placeholder);
